import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogBody } from '@/components/catalyst-ui/dialog'
import { XMarkIcon } from '@heroicons/react/24/outline'
import PricingIframe from './PricingIframe'
import LicenseAssignment from './LicenseAssignment'
import { useUIStore } from '@/state/ui.store'
import { useSelectionStore } from '@/state/selection.store'
import { usePricingAuth } from '../hooks/usePricingAuth'
import { getPricingIframeUrl } from '../api'
import { trackEvent } from '@/lib/analytics'
import { trackFunnel } from '@/lib/funnel'

/**
 * PricingIframeModal - A modal wrapper for the PricingIframe component.
 * Automatically listens to ui.store.showPricingModal for opening/closing.
 * Shows LicenseAssignment only when a project is selected (not at workspace level).
 */
export default function PricingIframeModal() {
  const show = useUIStore(state => state.showPricingModal)
  const hideLicenseAssignment = useUIStore(state => state.hidePricingModalLicenseAssignment)
  const setShowPricingModal = useUIStore(state => state.setShowPricingModal)
  // Caller can force the pricing iframe out of its "minimal" view for this
  // open — used by UnlimitedPromoCard's "Compare plans" CTA. Resets on close.
  const forceShowAllPlans = useUIStore(state => state.forcePricingModalShowAllPlans)
  // Which feature/gate opened the modal (set by setShowPricingModal(show, caseItem))
  const pricingModalCase = useUIStore(state => state.pricingModalCase)

  const activeProjectId = useSelectionStore(state => state.activeProjectId)
  const hasProjectSelected = !!activeProjectId

  const { getAuthPayload, workspace } = usePricingAuth()

  const [isScrolled, setIsScrolled] = useState(false)
  const lastScrollY = useRef(0)

  const onHide = () => {
    // Journeys `pricing_closed` (X / backdrop / Esc). Additive; the existing logic below is unchanged.
    trackFunnel('pricing_closed', { props: { feature: pricingModalCase || '', codebase: 'dashboard' } })
    trackEvent('Pricing:ModalClosed')
    setShowPricingModal(false)
  }

  // Track pricing modal open
  useEffect(() => {
    if (show) {
      trackEvent('Pricing:ModalOpened', {
        hasProjectSelected,
        feature: pricingModalCase || 'unknown',
        triggerSurface: 'dashboard_modal',
      })
    }
  }, [show, pricingModalCase])

  // Journeys `pricing_viewed`: exactly once per OPEN. Keyed on the closed->open transition only (the
  // PostHog effect above re-fires when the case changes mid-open; this one must not). The feature/auto
  // context is read from the store at that moment, so it is never stale and adds no dependencies.
  const funnelOpenRef = useRef(false)
  useEffect(() => {
    if (!show) {
      funnelOpenRef.current = false
      return
    }
    if (funnelOpenRef.current) return
    funnelOpenRef.current = true
    const { pricingModalCase: feature, pricingModalMeta: meta } = useUIStore.getState()
    trackFunnel('pricing_viewed', {
      props: {
        feature: feature || '',
        surface: 'modal',
        codebase: 'dashboard',
        autoOpened: Boolean(meta && meta.auto),
      },
    })
  }, [show])

  // Listen for scroll events from the iframe
  useEffect(() => {
    const handleIframeScroll = (event) => {
      if (event.data?.type === 'PRICING_IFRAME_SCROLL') {
        const scrollY = event.data.scrollY || 0
        const isScrollingDown = scrollY > lastScrollY.current
        lastScrollY.current = scrollY

        if (scrollY > 50 && isScrollingDown) {
          setIsScrolled(true)
        } else if (scrollY < 50) {
          setIsScrolled(false)
        }
      }
    }

    window.addEventListener('message', handleIframeScroll)
    return () => window.removeEventListener('message', handleIframeScroll)
  }, [])

  // Show LicenseAssignment only in project context
  const showLicenseAssignment = hasProjectSelected && !hideLicenseAssignment

  return (
    // size="6xl" is intentionally absent from the Dialog sizes map — it makes
    // the panel full-width (w-full base), which the pricing table needs.
    <Dialog open={show} onClose={onHide} size="6xl" className="!p-0 !bg-[#FAFAFA]" style={{ minHeight: '94vh' }}>
      <div style={{ position: 'relative' }}>
        {/* Solid backdrop so the X is visible over ANY content behind it —
            the old conditional white icon disappeared on the light panel. */}
        <button
          onClick={onHide}
          className="flex h-8 w-8 items-center justify-center rounded-full cursor-pointer border border-zinc-200 bg-white shadow-[0_1px_4px_rgba(24,24,27,0.12)] transition-colors hover:bg-zinc-100"
          aria-label="Close"
          style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 60 }}
        >
          <XMarkIcon className="w-5 h-5 text-zinc-700" />
        </button>
      </div>
      <DialogBody className="!p-0 !mt-0 !overflow-hidden rounded-2xl" style={{ display: 'flex', flexDirection: 'column', height: 'calc(94vh - 10px)', minHeight: '500px' }}>
        {showLicenseAssignment && (
          <LicenseAssignment isScrolled={isScrolled} />
        )}
        <div style={{ flex: 1, minHeight: '400px' }}>
          <PricingIframe
            pricingIframeUrl={getPricingIframeUrl()}
            getAuthPayload={getAuthPayload}
            source="pricing-modal"
            surface="modal"
            feature={pricingModalCase}
            workspace={workspace}
            style={{ height: '100%', minHeight: '400px' }}
            forceShowAllPlans={forceShowAllPlans}
          />
        </div>
      </DialogBody>
    </Dialog>
  )
}
